import type { Chargebee, ChargebeeInstance } from '@chargebee/chargebee-js-types'

import { useMutationChargebee } from './api/chargebee/useMutationChargebee'

export type Payload = Omit<App.Data.Payloads.Chargebee.PaymentIntentPayload, 'payment_method_type'>

interface PaymentPayload {
  payload: Payload
  iban: string
  firstName: string
  lastName: string
  email: string
}

export default () => {
  const { activeWorkspaceId } = useAuth()
  const { load } = useScript<typeof Chargebee>({
    src: 'https://js.chargebee.com/v2/chargebee.js',
  }, {
    trigger: 'manual',
    use() {
      return window.Chargebee
    },
  })

  const { data: config, error } = useQueryConfig.current()
  const { mutateAsync } = useMutationChargebee.createPaymentIntent()

  const ChargebeeInstance = shallowRef<ChargebeeInstance>()

  whenever(error, () => {
    throw new Error('Config could not be loaded')
  })

  const { stop } = watch(config, async (config) => {
    if (!config)
      return

    const chargebee = await load()
    chargebee.init({
      site: config.chargebee_site,
      publishableKey: config.chargebee_publishable_key,
    })
    ChargebeeInstance.value = chargebee.getInstance()
    nextTick(() => stop())
  }, { immediate: true })

  // TODO: DEV-8416 migrate functionality to BillingSepaForm or extend support for paypal etc.
  const createPaymentIntent = async ({ payload, iban, firstName, lastName, email }: PaymentPayload) => {
    if (!activeWorkspaceId.value || !ChargebeeInstance.value)
      return

    const handler = await ChargebeeInstance.value.load('direct_debit')
    const paymentIntent = await mutateAsync({
      workspaceId: activeWorkspaceId.value,
      payload: { ...payload, payment_method_type: 'direct_debit' },
    }).then(({ data }) => data)
    handler.setPaymentIntent(paymentIntent)

    const intent = await handler.handlePayment({
      customer: {
        firstName,
        lastName,
        email,
      },
      bankAccount: {
        iban,
      },
    })

    return intent
  }

  return {
    createPaymentIntent,
    instance: ChargebeeInstance,
  }
}
